import { useState, useMemo, useCallback } from 'react';
import {
  StabilizerCircuit,
  CircuitGate,
  CircuitQubit,
  GateType,
  GATE_QUBIT_COUNT,
  circuitId,
} from '../../types/stabilizer';

interface Props {
  circuit: StabilizerCircuit;
  stabName: string;
  onChange: (circuit: StabilizerCircuit) => void;
  onClose: () => void;
}

// Layout constants
const CELL_W = 56;
const CELL_H = 56;
const WIRE_Y_OFF = CELL_H / 2;
const LABEL_W = 100;
const CLASSICAL_H = 48; // Height of classical register section
const CLASSICAL_WIRE_GAP = 3;

const GATE_COLORS: Record<GateType, string> = {
  H: '#7c3aed',
  CX: '#2563eb',
  CZ: '#0891b2',
  M: '#dc2626',
  R: '#ca8a04',
};

const GATE_LABELS: Record<GateType, string> = {
  H: 'H',
  CX: 'CNOT',
  CZ: 'CZ',
  M: 'M',
  R: 'R',
};

export default function CircuitComposer({ circuit, stabName, onChange, onClose }: Props) {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const [draggingFrom, setDraggingFrom] = useState<string | null>(null);
  const [draggingNewGate, setDraggingNewGate] = useState<GateType | null>(null);
  const [selectedGateId, setSelectedGateId] = useState<string | null>(null);
  const [warningMsg, setWarningMsg] = useState<string | null>(null);
  const [pendingTwoQubit, setPendingTwoQubit] = useState<{ gateType: GateType; col: number; controlRow: number } | null>(null);

  const nQubits = circuit.qubits.length;
  const numCols = useMemo(() => {
    const maxCol = circuit.gates.reduce((m, g) => Math.max(m, g.col), -1);
    return Math.max(maxCol + 2, 8);
  }, [circuit.gates]);

  // Measurement gates sorted by column, with assigned classical bit indices
  const measureGates = useMemo(() => {
    return circuit.gates
      .filter((g) => g.gate === 'M')
      .sort((a, b) => a.col - b.col)
      .map((g, idx) => ({ ...g, classicalBit: idx }));
  }, [circuit.gates]);

  const numClassicalBits = measureGates.length;

  // Which classical bit is the syndrome output?
  const syndromeBit = circuit.syndromeClassicalBit ?? 0;

  // Map gate id → classical bit index for M gates
  const mGateBitMap = useMemo(() => {
    const map = new Map<string, number>();
    measureGates.forEach((mg) => map.set(mg.id, mg.classicalBit));
    return map;
  }, [measureGates]);

  // Index: "row-col" → gate
  const gatesByPos = useMemo(() => {
    const map = new Map<string, CircuitGate>();
    circuit.gates.forEach((g) => {
      g.qubitRows.forEach((r) => map.set(`${r}-${g.col}`, g));
    });
    return map;
  }, [circuit.gates]);

  const isCellOccupied = useCallback((row: number, col: number, excludeId?: string) => {
    const g = gatesByPos.get(`${row}-${col}`);
    return g ? (excludeId ? g.id !== excludeId : true) : false;
  }, [gatesByPos]);

  const showWarning = useCallback((msg: string) => {
    setWarningMsg(msg);
    setTimeout(() => setWarningMsg(null), 2000);
  }, []);

  // Set syndrome output to a specific M gate's classical bit
  const setSyndromeFromGate = useCallback((gateId: string) => {
    const bit = mGateBitMap.get(gateId);
    if (bit !== undefined) {
      onChange({ ...circuit, syndromeClassicalBit: bit });
    }
  }, [circuit, onChange, mGateBitMap]);

  // ── Drag handlers ──

  const handlePaletteDragStart = useCallback((e: React.DragEvent, gate: GateType) => {
    e.dataTransfer.setData('gateType', gate);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingNewGate(gate);
  }, []);

  const handleGateDragStart = useCallback((e: React.DragEvent, gate: CircuitGate) => {
    e.dataTransfer.setData('existingGateId', gate.id);
    e.dataTransfer.setData('gateType', gate.gate);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingFrom(gate.id);
    setSelectedGateId(gate.id);
  }, []);

  const handleGateDragEnd = useCallback(() => {
    setDraggingFrom(null);
    setDraggingNewGate(null);
  }, []);

  const handleCellDragOver = useCallback((e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = draggingFrom ? 'move' : 'copy';
    setHoveredCell({ row, col });
  }, [draggingFrom]);

  const handleCellDragLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  const handleCellDrop = useCallback((e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    setHoveredCell(null);

    const existingId = e.dataTransfer.getData('existingGateId');
    if (existingId) {
      const gate = circuit.gates.find((g) => g.id === existingId);
      if (!gate) return;
      if (gate.qubitRows.length > 1) {
        const blocked = gate.qubitRows.some((r) => isCellOccupied(r, col, existingId));
        if (blocked) { showWarning('Position occupied'); return; }
        onChange({ ...circuit, gates: circuit.gates.map((g) => g.id === existingId ? { ...g, col } : g) });
      } else {
        if (isCellOccupied(row, col, existingId)) { showWarning('Position occupied'); return; }
        onChange({ ...circuit, gates: circuit.gates.map((g) => g.id === existingId ? { ...g, col, qubitRows: [row] } : g) });
      }
      setDraggingFrom(null);
      return;
    }

    const gateType = e.dataTransfer.getData('gateType') as GateType;
    if (!gateType) return;
    if (isCellOccupied(row, col)) { showWarning('Position occupied'); return; }

    if (GATE_QUBIT_COUNT[gateType] === 2) {
      setPendingTwoQubit({ gateType, col, controlRow: row });
    } else {
      const newGates = [...circuit.gates, { id: circuitId(), gate: gateType, col, qubitRows: [row] }];
      onChange({ ...circuit, gates: newGates });
    }
    setDraggingNewGate(null);
  }, [circuit, onChange, isCellOccupied, showWarning]);

  const handleCellClick = useCallback((row: number, _col: number) => {
    if (pendingTwoQubit) {
      const { gateType, col: gCol, controlRow } = pendingTwoQubit;
      if (row === controlRow) { showWarning('Target must be a different qubit'); return; }
      if (isCellOccupied(row, gCol)) { showWarning('Target position occupied'); return; }
      onChange({
        ...circuit,
        gates: [...circuit.gates, { id: circuitId(), gate: gateType, col: gCol, qubitRows: [controlRow, row] }],
      });
      setPendingTwoQubit(null);
      return;
    }
    setSelectedGateId(null);
  }, [pendingTwoQubit, circuit, onChange, isCellOccupied, showWarning]);

  const handleGateClick = useCallback((e: React.MouseEvent, gate: CircuitGate) => {
    e.stopPropagation();
    if (pendingTwoQubit) return;
    // Clicking an M gate sets it as syndrome output
    if (gate.gate === 'M') {
      setSyndromeFromGate(gate.id);
    }
    setSelectedGateId(selectedGateId === gate.id ? null : gate.id);
  }, [selectedGateId, pendingTwoQubit, setSyndromeFromGate]);

  const deleteGate = useCallback((gateId: string) => {
    onChange({ ...circuit, gates: circuit.gates.filter((g) => g.id !== gateId) });
    setSelectedGateId(null);
  }, [circuit, onChange]);

  const cancelPending = useCallback(() => {
    setPendingTwoQubit(null);
  }, []);

  // ── Ancilla management ──

  const addAncilla = useCallback(() => {
    const ancillas = circuit.qubits.filter((q) => q.isAncilla);
    const maxIdx = ancillas.length > 0
      ? Math.max(...ancillas.map((q) => q.globalIndex)) + 1
      : circuit.qubits.filter((q) => !q.isAncilla).length;
    const newQ: CircuitQubit = {
      id: circuitId('anc'),
      label: `a${maxIdx}`,
      isAncilla: true,
      globalIndex: maxIdx,
    };
    onChange({ ...circuit, qubits: [...circuit.qubits, newQ] });
  }, [circuit, onChange]);

  const removeQubit = useCallback((qIdx: number) => {
    const q = circuit.qubits[qIdx];
    if (!q || !q.isAncilla) return;
    const newQubits = circuit.qubits.filter((_, i) => i !== qIdx);
    const newGates = circuit.gates
      .filter((g) => !g.qubitRows.includes(qIdx))
      .map((g) => ({ ...g, qubitRows: g.qubitRows.map((r) => (r > qIdx ? r - 1 : r)) }));
    onChange({ ...circuit, qubits: newQubits, gates: newGates });
  }, [circuit, onChange]);

  // ── Render ──

  const canvasW = LABEL_W + numCols * CELL_W;
  const quantumH = nQubits * CELL_H;
  const canvasH = quantumH + CLASSICAL_H;

  const renderCell = (row: number, col: number) => {
    const key = `${row}-${col}`;
    const gate = gatesByPos.get(key);
    const isDragging = draggingNewGate || draggingFrom;
    const isHovered = hoveredCell?.row === row && hoveredCell?.col === col && isDragging;
    const isBeingDragged = gate && draggingFrom === gate.id;
    const isSelected = gate ? selectedGateId === gate.id : false;
    const isPendingTarget = pendingTwoQubit && pendingTwoQubit.col === col && pendingTwoQubit.controlRow === row;
    const isPendingMode = !!pendingTwoQubit;
    const isPendingTargetCandidate = pendingTwoQubit && row !== pendingTwoQubit.controlRow;
    const isControlQubit = gate && gate.qubitRows.length > 1 && gate.qubitRows[0] === row;
    const isOccupiedHover = isHovered && gate && draggingFrom !== gate?.id;

    // Is this M gate the syndrome output?
    const isSyndromeM = gate && gate.gate === 'M' && mGateBitMap.get(gate.id) === syndromeBit;

    return (
      <div
        key={key}
        className={`relative flex items-center justify-center transition-colors border-r border-b border-gray-800/30 ${
          isOccupiedHover ? 'bg-red-900/30' : isHovered ? 'bg-indigo-900/30' : ''
        } ${isBeingDragged ? 'opacity-30' : ''} ${
          isPendingMode && isPendingTargetCandidate ? 'bg-yellow-900/10 cursor-pointer' : ''
        } ${isPendingTarget ? 'bg-yellow-600/20' : ''}`}
        style={{ width: CELL_W, height: CELL_H }}
        onDragOver={(e) => handleCellDragOver(e, row, col)}
        onDragLeave={handleCellDragLeave}
        onDrop={(e) => handleCellDrop(e, row, col)}
        onClick={() => handleCellClick(row, col)}
      >
        {!gate && !isPendingMode && (
          <div className={`absolute inset-2 rounded border-2 border-dashed transition-colors ${
            isHovered ? 'border-indigo-500 bg-indigo-900/10' : 'border-transparent'
          }`} />
        )}

        {/* Gate visualization (target qubit for multi-qubit) */}
        {gate && !isControlQubit && (
          <div
            className={`relative group ${isSelected ? 'ring-2 ring-indigo-400 ring-offset-1 ring-offset-gray-950 rounded' : ''}`}
            draggable
            onDragStart={(e) => handleGateDragStart(e, gate)}
            onDragEnd={handleGateDragEnd}
            onClick={(e) => handleGateClick(e, gate)}
            style={{ cursor: 'grab' }}
          >
            <div
              className={`w-10 h-10 rounded flex items-center justify-center text-white text-xs font-bold shadow-lg ${
                isSyndromeM ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-gray-950' : ''
              }`}
              style={{ backgroundColor: GATE_COLORS[gate.gate] || '#6b7280' }}
            >
              {gate.gate === 'CX' ? (
                <svg width="28" height="28" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r="11" fill="none" stroke="white" strokeWidth="2" />
                  <line x1="7" y1="14" x2="21" y2="14" stroke="white" strokeWidth="2" />
                  <line x1="14" y1="7" x2="14" y2="21" stroke="white" strokeWidth="2" />
                </svg>
              ) : gate.gate === 'CZ' ? 'Z' : gate.gate}
            </div>

            {/* Syndrome badge on M gates */}
            {gate.gate === 'M' && (
              <div className={`absolute -bottom-1 -right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[9px] font-bold px-1 ${
                isSyndromeM
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-700 text-gray-400'
              }`} title={isSyndromeM ? 'Syndrome output (click another M to change)' : 'Click to set as syndrome output'}>
                c{mGateBitMap.get(gate.id) ?? '?'}
              </div>
            )}

            {/* Delete button */}
            <button
              onClick={(e) => { e.stopPropagation(); deleteGate(gate.id); }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10 flex items-center justify-center"
              title="Remove gate"
            >
              x
            </button>
          </div>
        )}

        {/* Control dot */}
        {gate && isControlQubit && (
          <div
            className="relative group"
            draggable
            onDragStart={(e) => handleGateDragStart(e, gate)}
            onDragEnd={handleGateDragEnd}
            onClick={(e) => handleGateClick(e, gate)}
            style={{ cursor: 'grab' }}
          >
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: GATE_COLORS[gate.gate] || '#6b7280' }} />
          </div>
        )}
      </div>
    );
  };

  // SVG: multi-qubit connections
  const renderConnections = () => {
    return circuit.gates
      .filter((g) => g.qubitRows.length > 1)
      .map((g) => {
        const minRow = Math.min(...g.qubitRows);
        const maxRow = Math.max(...g.qubitRows);
        const x = LABEL_W + g.col * CELL_W + CELL_W / 2;
        const y1 = minRow * CELL_H + WIRE_Y_OFF;
        const y2 = maxRow * CELL_H + WIRE_Y_OFF;
        const isBeingDragged = draggingFrom === g.id;
        return (
          <line key={`conn-${g.id}`} x1={x} y1={y1} x2={x} y2={y2}
            stroke={GATE_COLORS[g.gate] || '#6b7280'} strokeWidth={2}
            opacity={isBeingDragged ? 0.3 : 1} />
        );
      });
  };

  // SVG: measurement connections to classical register
  const renderMeasurementConnections = () => {
    const classicalY = quantumH + CLASSICAL_H / 2;

    return measureGates.map((mg) => {
      const qubitRow = mg.qubitRows[0];
      const x = LABEL_W + mg.col * CELL_W + CELL_W / 2;
      const y1 = qubitRow * CELL_H + WIRE_Y_OFF + 22; // below M gate
      const y2 = classicalY;
      const isSyndrome = mg.classicalBit === syndromeBit;
      const isBeingDragged = draggingFrom === mg.id;

      return (
        <g key={`meas-${mg.id}`} opacity={isBeingDragged ? 0.3 : 1}>
          {/* Vertical line */}
          <line x1={x} y1={y1} x2={x} y2={y2} stroke={isSyndrome ? '#22c55e' : '#6b7280'} strokeWidth={1} />
          {/* Arrow */}
          <polygon
            points={`${x - 4},${y2 - 6} ${x + 4},${y2 - 6} ${x},${y2}`}
            fill={isSyndrome ? '#22c55e' : '#6b7280'}
          />
          {/* Classical bit label */}
          <text x={x + 8} y={y2 + 4} fontSize="10" fill={isSyndrome ? '#22c55e' : '#6b7280'} fontFamily="monospace">
            c[{mg.classicalBit}]
          </text>
          {/* Syndrome star indicator */}
          {isSyndrome && (
            <g>
              <circle cx={x} cy={y2 + 14} r={8} fill="#22c55e" />
              <text x={x} y={y2 + 18} textAnchor="middle" fontSize="9" fill="white" fontWeight="bold">S</text>
            </g>
          )}
        </g>
      );
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-950 border-t border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-white">Circuit Composer</h3>
          <span className="text-xs text-gray-400">{stabName}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Syndrome info */}
          {numClassicalBits > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-900/40 border border-green-700/40 text-green-400">
                <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="#22c55e"/></svg>
                Syndrome: c[{syndromeBit}]
              </span>
              <span className="text-gray-500">Click M gate to change</span>
            </div>
          )}
          <button onClick={onClose}
            className="px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors">
            Done
          </button>
        </div>
      </div>

      {/* Gate palette */}
      <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/50 border-b border-gray-800/50 shrink-0">
        <span className="text-xs text-gray-500 mr-1">Drag gates:</span>
        {(['H', 'CX', 'CZ', 'M', 'R'] as GateType[]).map((g) => (
          <div
            key={g}
            draggable
            onDragStart={(e) => handlePaletteDragStart(e, g)}
            onDragEnd={handleGateDragEnd}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-mono font-bold cursor-grab active:cursor-grabbing hover:scale-105 transition-transform select-none"
            style={{ backgroundColor: GATE_COLORS[g] + '30', color: GATE_COLORS[g], border: `1px solid ${GATE_COLORS[g]}50` }}
            title={`Drag ${GATE_LABELS[g]} onto circuit (${GATE_QUBIT_COUNT[g]} qubit${GATE_QUBIT_COUNT[g] > 1 ? 's' : ''})`}
          >
            {g}
            <span className="text-[10px] font-normal opacity-60">({GATE_QUBIT_COUNT[g]}q)</span>
          </div>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={addAncilla}
            className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-gray-300 transition-colors">
            + Ancilla
          </button>
        </div>
      </div>

      {/* Pending 2-qubit banner */}
      {pendingTwoQubit && (
        <div className="flex items-center gap-3 px-4 py-2 bg-yellow-900/30 border-b border-yellow-700/30 shrink-0">
          <span className="text-xs text-yellow-300 animate-pulse font-medium">
            Click a target qubit wire for {pendingTwoQubit.gateType} (control on row {pendingTwoQubit.controlRow})
          </span>
          <button onClick={cancelPending} className="text-xs text-gray-400 hover:text-white underline">Cancel</button>
        </div>
      )}

      {/* Circuit grid */}
      <div className="flex-1 overflow-auto relative">
        <div className="relative" style={{ minWidth: canvasW, minHeight: canvasH }}>
          {/* SVG layer for wires, connections, measurement lines */}
          <svg className="absolute top-0 left-0 pointer-events-none" width={canvasW} height={canvasH}>
            {/* Qubit wires */}
            {circuit.qubits.map((q, i) => (
              <line key={`wire-${i}`}
                x1={LABEL_W} y1={i * CELL_H + WIRE_Y_OFF}
                x2={canvasW} y2={i * CELL_H + WIRE_Y_OFF}
                stroke={q.isAncilla ? '#4b5563' : '#374151'} strokeWidth={1.5}
                strokeDasharray={q.isAncilla ? '4 3' : undefined} />
            ))}

            {/* Classical register double wire */}
            {numClassicalBits > 0 && (
              <>
                <line x1={LABEL_W} y1={quantumH + CLASSICAL_H / 2 - CLASSICAL_WIRE_GAP}
                  x2={canvasW} y2={quantumH + CLASSICAL_H / 2 - CLASSICAL_WIRE_GAP}
                  stroke="#4b5563" strokeWidth={1} />
                <line x1={LABEL_W} y1={quantumH + CLASSICAL_H / 2 + CLASSICAL_WIRE_GAP}
                  x2={canvasW} y2={quantumH + CLASSICAL_H / 2 + CLASSICAL_WIRE_GAP}
                  stroke="#4b5563" strokeWidth={1} />
                {/* Slash mark */}
                <line x1={LABEL_W + 12} y1={quantumH + CLASSICAL_H / 2 - 8}
                  x2={LABEL_W + 22} y2={quantumH + CLASSICAL_H / 2 + 8}
                  stroke="#4b5563" strokeWidth={1} />
                <text x={LABEL_W + 27} y={quantumH + CLASSICAL_H / 2 - 8}
                  fontSize="10" fill="#6b7280" fontFamily="monospace">{numClassicalBits}</text>
              </>
            )}

            {/* Multi-qubit connections */}
            {renderConnections()}

            {/* Measurement connections to classical register */}
            {renderMeasurementConnections()}
          </svg>

          {/* Grid: labels + cells */}
          <div className="relative flex">
            {/* Qubit labels */}
            <div className="flex flex-col shrink-0 bg-gray-900 border-r border-gray-800 z-10" style={{ width: LABEL_W }}>
              {circuit.qubits.map((q, i) => (
                <div key={q.id}
                  className="flex items-center gap-1.5 px-2 font-mono text-xs border-b border-gray-800/30"
                  style={{ height: CELL_H }}>
                  <span className={q.isAncilla ? 'text-slate-400' : 'text-gray-400'}>{q.label}</span>
                  <span className={`text-[9px] ${q.isAncilla ? 'text-slate-600' : 'text-gray-600'}`}>
                    ({q.isAncilla ? 'anc' : 'data'})
                  </span>
                  {q.isAncilla && (
                    <button onClick={() => removeQubit(i)}
                      className="ml-auto w-4 h-4 rounded text-gray-600 hover:text-red-400 hover:bg-red-900/30 flex items-center justify-center text-[10px] transition-colors"
                      title="Remove ancilla">x</button>
                  )}
                </div>
              ))}
              {/* Classical register label */}
              {numClassicalBits > 0 && (
                <div className="flex items-center px-2 font-mono text-xs text-gray-500 border-t border-gray-700"
                  style={{ height: CLASSICAL_H }}>
                  c[0..{numClassicalBits - 1}]
                </div>
              )}
            </div>

            {/* Gate cells */}
            <div className="flex-1">
              {Array.from({ length: nQubits }, (_, row) => (
                <div key={`row-${row}`} className="flex">
                  {Array.from({ length: numCols }, (_, col) => renderCell(row, col))}
                </div>
              ))}
              {/* Classical register row (visual only, no drop) */}
              {numClassicalBits > 0 && (
                <div className="flex border-t border-gray-700" style={{ height: CLASSICAL_H }} />
              )}
            </div>
          </div>
        </div>

        {/* Drag hint */}
        {draggingFrom && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
            Drop to reposition gate
          </div>
        )}

        {/* Selection info */}
        {selectedGateId && !draggingFrom && !pendingTwoQubit && (
          <div className="absolute bottom-2 left-2 bg-indigo-600 text-white text-xs px-3 py-1 rounded-full">
            Gate selected - press Delete or click x to remove
          </div>
        )}

        {/* Warning */}
        {warningMsg && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs px-4 py-1.5 rounded-lg shadow-lg animate-pulse z-20">
            {warningMsg}
          </div>
        )}
      </div>
    </div>
  );
}
